/**
 * Sidebar - Desktop navigation (≥768px)
 *
 * Features:
 * - 240px expanded, 64px collapsed
 * - Collapsible with smooth animation
 * - Teal accent for active items
 * - Logo + primary nav + secondary nav
 */

import { useState } from 'react';
import { Activity, LayoutGrid, Palette, ChevronLeft, ChevronRight, Search, Plus, Command, Settings } from 'lucide-react';
import { ArdyStar } from '@/components/ui/ArdyStar';
import { NavItem } from '@/components/ui/NavItem';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '@/hooks/useSubscription';

export function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const { isPremium } = useSubscription();

  const handleCommandPalette = () => {
    const event = new KeyboardEvent('keydown', {
      key: 'k',
      code: 'KeyK',
      metaKey: true,
      ctrlKey: true,
      bubbles: true,
    });
    window.dispatchEvent(event);
  };

  return (
    <aside
      className={`
        fixed left-0 top-0 h-screen bg-transparent flex flex-col
        transition-all duration-300 ease-in-out z-sticky
        ${collapsed ? 'w-16' : 'w-64'}
      `}
      aria-label="Main navigation"
    >
      {/* Logo / Brand - Compact */}
      <div className="h-14 flex items-center justify-between px-3 border-b border-cream-300">
        {!collapsed && (
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-teal-500 flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-xs">TP</span>
            </div>
            <div className="leading-tight">
              <div className="text-xs font-semibold text-charcoal-800">TruePrice</div>
              <div className="text-[10px] text-charcoal-500">Pricing, defensible.</div>
            </div>
          </div>
        )}

        {/* Collapse Toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className={`
            w-6 h-6 rounded flex items-center justify-center
            hover:bg-cream-200 transition-colors
            ${collapsed ? 'mx-auto' : ''}
          `}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!collapsed}
        >
          {collapsed ? (
            <ChevronRight className="w-3.5 h-3.5 text-charcoal-600" />
          ) : (
            <ChevronLeft className="w-3.5 h-3.5 text-charcoal-600" />
          )}
        </button>
      </div>

      {/* Search Actions - Compact */}
      {!collapsed && (
        <div className="px-3 pt-2">
          <button
            onClick={handleCommandPalette}
            className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-cream-100 border border-cream-300 text-charcoal-600 hover:bg-cream-200 transition-colors"
          >
            <Search className="w-3.5 h-3.5" />
            <span className="text-xs flex-1 text-left">Search actions…</span>
            <kbd className="px-1.5 py-0.5 text-[10px] bg-cream-100 border border-cream-300 rounded">⌘K</kbd>
          </button>
        </div>
      )}

      {/* Primary Navigation - Compact */}
      <nav className="flex-1 px-2 py-2 space-y-0.5 overflow-y-auto" aria-label="Primary">
        <NavItem to="/app/dashboard" icon={Activity} label="Dashboard" collapsed={collapsed} />
        <NavItem to="/app/portfolio" icon={LayoutGrid} label="Portfolio" collapsed={collapsed} />
        <NavItem to="/app/studio" icon={Palette} label="Studio" collapsed={collapsed} />
      </nav>

      {/* Secondary Navigation */}
      <div className="px-2 pb-1">
        <div className="h-px bg-cream-300/60 mb-1" />
        <button
          onClick={() => window.dispatchEvent(new CustomEvent('open-settings'))}
          className="w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-xs transition-colors text-charcoal-500 hover:bg-cream-200 hover:text-charcoal-700"
          aria-label="Settings"
        >
          <Settings className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
          {!collapsed && <span className="truncate">Settings</span>}
        </button>
      </div>

      {/* Quick Add Section - Compact with label inside card */}
      {!collapsed && (
        <div className="px-3 pb-2 pt-2">
          <div className="bg-cream-100 rounded-xl border border-cream-300/60 shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-3 space-y-2">
            <div className="text-[10px] font-semibold text-charcoal-600 uppercase tracking-wide mb-1">Quick add</div>
            <button
              onClick={() => navigate('/app/calculate')}
              className="w-full flex items-center gap-1.5 px-3 py-2 bg-charcoal-800 text-white rounded-lg hover:bg-charcoal-700 transition-colors text-xs font-semibold"
            >
              <Plus className="w-3.5 h-3.5" />
              New Price
            </button>
            <button
              onClick={handleCommandPalette}
              className="w-full flex items-center gap-1.5 px-3 py-2 bg-cream-100 text-charcoal-800 rounded-lg hover:bg-cream-200 transition-colors text-xs font-medium"
            >
              <Command className="w-3.5 h-3.5" />
              Open command
            </button>
          </div>
        </div>
      )}


      {/* Subscription Badge */}
      {!collapsed && (
        <div className="px-3 pb-2">
          {isPremium ? (
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal-50 border border-teal-200">
              <ArdyStar size={12} color="#0d9488" />
              <span className="text-[11px] font-semibold text-teal-700">Premium</span>
            </div>
          ) : (
            <div className="flex items-center justify-between px-3 py-1.5 rounded-lg bg-cream-100 border border-cream-300">
              <span className="text-[11px] font-medium text-charcoal-500">Free</span>
              <button
                onClick={() => window.dispatchEvent(new CustomEvent('open-settings'))}
                className="text-[11px] font-semibold text-teal-600 hover:text-teal-700 transition-colors"
              >
                Upgrade
              </button>
            </div>
          )}
        </div>
      )}

      {/* Bottom padding for safe area */}
      <div className="h-4" style={{ height: 'var(--safe-area-inset-bottom, 0px)' }} />
    </aside>
  );
}

export default Sidebar;
